import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { ExamAnswer, ExamDocument } from "@/contexts/ExamModeContext";
import type { Task } from "@/contexts/TaskContext";

export interface ExamSession {
  id: string;
  user_id: string;
  document_type: string;
  year: number;
  started_at: string;
  completed_at: string | null;
  time_spent_seconds: number | null;
  total_auto_points: number;
  max_auto_points: number;
  total_open_points: number;
  max_open_points: number;
  is_completed: boolean;
  created_at: string;
  updated_at: string;
}

export interface ExamSessionTask {
  id: string;
  session_id: string;
  task_id: string;
  task_number: number | null;
  subpoint: number | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  user_answer: any; // JSON from DB, can be ExamAnswer or null
  task_type: string | null;
  is_correct: boolean | null;
  points_earned: number;
  max_points: number;
  open_task_response_id: string | null;
  created_at: string;
  // Joined from open_task_responses
  open_task_grade?: string | null;
  open_task_admin_comment?: string | null;
}

// Extended task with full task details for history view
export interface ExamSessionTaskWithDetails extends ExamSessionTask {
  task_details?: {
    instruction?: string;
    question?: string;
    answers?: string[];
    correct_index?: number;
    image_url?: string;
    image_urls?: string[];
    task_subtype?: string;
    is_multi_select?: boolean;
    use_yes_no?: boolean;
    matching_data?: any;
    table_data?: any;
    model_answer?: string;
  };
}

export interface ExamSessionWithTaskDetails extends ExamSession {
  tasks: ExamSessionTaskWithDetails[];
}

export interface ExamSessionWithTasks extends ExamSession {
  tasks: ExamSessionTask[];
}

// Query keys
export const examSessionKeys = {
  all: ["exam-sessions"] as const,
  list: (userId?: string) => [...examSessionKeys.all, "list", userId] as const,
  detail: (sessionId: string) => [...examSessionKeys.all, "detail", sessionId] as const,
};

// Fetch all exam sessions for current user
export function useExamSessionsQuery() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: examSessionKeys.list(user?.id),
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from("exam_sessions")
        .select("*")
        .eq("user_id", user.id)
        .order("started_at", { ascending: false });
      
      if (error) throw error;
      // Filter out sessions with 0 total points (incomplete/empty attempts)
      return (data as ExamSession[]).filter(s => 
        (s.total_auto_points || 0) + (s.total_open_points || 0) > 0 || (s.max_auto_points || 0) + (s.max_open_points || 0) === 0
      );
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });
}

// Fetch single exam session with all tasks and their full details
export function useExamSessionDetailQuery(sessionId: string | null) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: examSessionKeys.detail(sessionId ?? ""),
    queryFn: async () => {
      if (!sessionId || !user) return null;
      
      const [sessionResult, tasksResult] = await Promise.all([
        supabase
          .from("exam_sessions")
          .select("*")
          .eq("id", sessionId)
          .single(),
        supabase
          .from("exam_session_tasks")
          .select(`
            *,
            open_task_responses:open_task_response_id (
              grade,
              admin_comment
            ),
            tasks:task_id (
              instruction,
              question,
              answers,
              correct_index,
              image_url,
              image_urls,
              task_subtype,
              is_multi_select,
              use_yes_no,
              matching_data,
              table_data,
              model_answer
            )
          `)
          .eq("session_id", sessionId)
          .order("task_number", { ascending: true })
          .order("subpoint", { ascending: true }),
      ]);
      
      if (sessionResult.error) throw sessionResult.error;
      if (tasksResult.error) throw tasksResult.error;
      
      // Transform tasks to flatten the joined data
      const tasks = tasksResult.data.map((task: any) => ({
        ...task,
        open_task_grade: task.open_task_responses?.grade || null,
        open_task_admin_comment: task.open_task_responses?.admin_comment || null,
        open_task_responses: undefined, // Remove nested object
        task_details: task.tasks ? {
          instruction: task.tasks.instruction,
          question: task.tasks.question,
          answers: task.tasks.answers,
          correct_index: task.tasks.correct_index,
          image_url: task.tasks.image_url,
          image_urls: task.tasks.image_urls,
          task_subtype: task.tasks.task_subtype,
          is_multi_select: task.tasks.is_multi_select,
          use_yes_no: task.tasks.use_yes_no,
          matching_data: task.tasks.matching_data,
          table_data: task.tasks.table_data,
          model_answer: task.tasks.model_answer,
        } : undefined,
        tasks: undefined, // Remove nested object
      })) as ExamSessionTaskWithDetails[];
      
      return {
        ...sessionResult.data,
        tasks,
      } as ExamSessionWithTaskDetails;
    },
    enabled: !!sessionId && !!user,
    staleTime: 2 * 60 * 1000,
  });
}

// Create new exam session
export function useCreateExamSessionMutation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async ({ document, tasks }: { document: ExamDocument; tasks: Task[] }) => {
      if (!user) throw new Error("User not authenticated");
      
      // Create exam session
      const { data: session, error: sessionError } = await supabase
        .from("exam_sessions")
        .insert({
          user_id: user.id,
          document_type: document.documentType,
          year: document.year,
          started_at: new Date().toISOString(),
          is_completed: false,
        })
        .select()
        .single();
      
      if (sessionError) throw sessionError;
      
      return session as ExamSession;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: examSessionKeys.all });
    },
  });
}

// Complete exam session with all answers
export function useCompleteExamSessionMutation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async ({
      sessionId,
      answers,
      timeSpentSeconds,
      examTasks,
    }: {
      sessionId: string;
      answers: Map<string, ExamAnswer>;
      timeSpentSeconds: number;
      examTasks: Task[];
    }) => {
      if (!user) throw new Error("User not authenticated");
      
      // Calculate totals
      let totalAutoPoints = 0;
      let maxAutoPoints = 0;
      let maxOpenPoints = 0;
      
      // Prepare task records
      const taskRecords: Omit<ExamSessionTask, 'id' | 'created_at'>[] = [];
      
      for (const task of examTasks) {
        const answer = answers.get(task.id);
        const maxPoints = task.isTwoPoints ? 2 : 1;
        // Check for open task - taskSubtype is 'zadanie otwarte' or task type is 'otwarte'
        const isOpenTask = task.taskSubtype === 'zadanie otwarte' || task.taskType === 'otwarte';
        
        if (isOpenTask) {
          maxOpenPoints += maxPoints;
        } else {
          maxAutoPoints += maxPoints;
          if (answer?.earnedPoints !== undefined) {
            totalAutoPoints += answer.earnedPoints;
          }
        }
        
        taskRecords.push({
          session_id: sessionId,
          task_id: task.id,
          task_number: task.taskNumber || null,
          subpoint: task.subpoint || null,
          task_type: isOpenTask ? 'open' : 'auto',
          user_answer: answer || null,
          is_correct: answer?.isCorrect ?? null,
          points_earned: answer?.earnedPoints || 0,
          max_points: maxPoints,
          open_task_response_id: null, // Will be linked when open task response is created
        });
      }
      
      // Insert all task records
      const { error: tasksError } = await supabase
        .from("exam_session_tasks")
        .insert(taskRecords as any[]);
      
      if (tasksError) throw tasksError;
      
      // Update session as completed
      const { data: updatedSession, error: updateError } = await supabase
        .from("exam_sessions")
        .update({
          is_completed: true,
          completed_at: new Date().toISOString(),
          time_spent_seconds: timeSpentSeconds,
          total_auto_points: totalAutoPoints,
          max_auto_points: maxAutoPoints,
          max_open_points: maxOpenPoints,
          updated_at: new Date().toISOString(),
        })
        .eq("id", sessionId)
        .select()
        .single();
      
      if (updateError) throw updateError;
      
      return updatedSession as ExamSession;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: examSessionKeys.all });
    },
  });
}

// Link open task response to exam session
export function useLinkOpenTaskToSessionMutation() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({
      sessionId,
      taskId,
      responseId,
    }: {
      sessionId: string;
      taskId: string;
      responseId: string;
    }) => {
      // Update open_task_responses with session id
      const { error: responseError } = await supabase
        .from("open_task_responses")
        .update({ exam_session_id: sessionId })
        .eq("id", responseId);
      
      if (responseError) throw responseError;
      
      // Update exam_session_tasks with response id
      const { error: taskError } = await supabase
        .from("exam_session_tasks")
        .update({ open_task_response_id: responseId })
        .eq("session_id", sessionId)
        .eq("task_id", taskId);
      
      if (taskError) throw taskError;
    },
    onSuccess: (_, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: examSessionKeys.detail(sessionId) });
    },
  });
}

// Delete exam session with all related data
export function useDeleteExamSessionMutation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async (sessionId: string) => {
      if (!user) throw new Error("User not authenticated");
      
      // First, get all open task response IDs linked to this session
      const { data: tasks } = await supabase
        .from("exam_session_tasks")
        .select("open_task_response_id")
        .eq("session_id", sessionId)
        .not("open_task_response_id", "is", null);
      
      const responseIds = tasks?.map(t => t.open_task_response_id).filter(Boolean) || [];
      
      // Delete open task responses linked to this session
      if (responseIds.length > 0) {
        const { error: responsesError } = await supabase
          .from("open_task_responses")
          .delete()
          .in("id", responseIds);
        
        if (responsesError) throw responsesError;
      }
      
      // Delete exam session tasks
      const { error: tasksError } = await supabase
        .from("exam_session_tasks")
        .delete()
        .eq("session_id", sessionId);
      
      if (tasksError) throw tasksError;
      
      // Delete the exam session itself
      const { error: sessionError } = await supabase
        .from("exam_sessions")
        .delete()
        .eq("id", sessionId);
      
      if (sessionError) throw sessionError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: examSessionKeys.all });
    },
  });
}
